// GtTaxonomyFields — the single source for the two controlled vocabularies.
//
// Every screen that touches a sector or an expertise domain imports from here,
// including screens not yet built. The point is that the list, the fetch, the
// admin-only create and the wording live in ONE place: the seven hardcoded
// domains that drifted out of step with the database are exactly what happens
// when each form keeps its own copy.
//
// Usage in any form, create or edit:
//   <SectorField value={form.sector} onChange={v => setForm({...form, sector: v})} authFetch={authFetch} />
//   <ExpertiseField value={form.expertise_domain} onChange={...} authFetch={authFetch} flash={flash} />
//   <ClientField value={form.client_id} onChange={...} authFetch={authFetch} flash={flash} />
import { useState, useEffect, useCallback } from 'react';
import GtCombo from './GtCombo';

// Fetch once per mounted form. Deliberately not a module-level cache: a domain
// added in one tab must appear in another without a reload.
function useList(authFetch, path, pick) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    if (!authFetch) return;
    authFetch(path).then(r => setItems(pick(r) || [])).catch(() => {});
  }, [authFetch, path, pick]);
  useEffect(() => { load(); }, [load]);
  return [items, setItems, load];
}

const pickSectors = (r) => (r && r.sectors) || [];
const pickDomains = (r) => (r && r.domains) || [];
const pickClients = (r) => (Array.isArray(r) ? r : []);

// Sector: curated but open. The column is a plain VARCHAR and no list can
// anticipate every niche, so a typed value is accepted.
export function SectorField({ value, onChange, authFetch, placeholder = 'Select a sector', disabled }) {
  const [sectors] = useList(authFetch, '/api/sectors', pickSectors);
  return (
    <GtCombo value={value || ''} options={sectors} onChange={onChange}
      placeholder={placeholder} allowFree disabled={disabled} />
  );
}

// Expertise domain: closed. Both candidates and roles carry a foreign key onto
// the slug, so a typed value would be rejected by the API. New entries are
// created as real rows, admin only, which the 403 reports plainly.
export function ExpertiseField({ value, onChange, authFetch, flash, placeholder = 'Select a domain', disabled }) {
  const [domains, setDomains] = useList(authFetch, '/api/expertise-domains', pickDomains);

  async function create(label) {
    try {
      const created = await authFetch('/api/expertise-domains', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label }),
      });
      setDomains(d => [...d, created].sort((a, b) => a.label.localeCompare(b.label)));
      if (flash) flash('Domain added');
      return created.slug;
    } catch (e) {
      // A 409 carries the existing row, so select it rather than failing.
      if (e.status === 409 && e.body && e.body.existing) {
        if (flash) flash('That domain already exists, selected it');
        return e.body.existing.slug;
      }
      if (flash) flash(e.message);
      return null;
    }
  }

  return (
    <GtCombo value={value || ''} options={domains.map(d => ({ value: d.slug, label: d.label }))}
      onChange={onChange} placeholder={placeholder} disabled={disabled}
      onCreate={create} createLabel="Add domain"
      hint="A new domain has no interview rubric yet and falls back to general." />
  );
}

// Client: closed, and always by id. A typed client name is not a link, which is
// what left roles orphaned from their client record.
export function ClientField({ value, onChange, authFetch, flash, placeholder = 'Select a client', disabled }) {
  const [clients, setClients] = useList(authFetch, '/api/clients', pickClients);

  async function create(name, extra) {
    try {
      // The website is captured here, not later. Intelligence sweeps, the
      // public blurb and the advert opener all key off the domain, so a client
      // created without one is inert until someone remembers to go back.
      let website = String((extra && extra.website) || '').trim();
      if (website && !/^https?:\/\//i.test(website)) website = 'https://' + website;
      const created = await authFetch('/api/clients', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(website ? { name, website } : { name }),
      });
      setClients(cs => [{ id: created.id, name: created.name }, ...cs]);
      if (flash) flash('Client added');
      return String(created.id);
    } catch (e) {
      if (e.status === 409 && e.body && e.body.existing) {
        const ex = e.body.existing;
        setClients(cs => cs.some(c => String(c.id) === String(ex.id)) ? cs : [{ id: ex.id, name: ex.name }, ...cs]);
        if (flash) flash('That client already exists, selected it');
        return String(ex.id);
      }
      if (flash) flash(e.message);
      return null;
    }
  }

  return (
    <GtCombo value={value == null ? '' : String(value)}
      options={clients.map(c => ({ value: String(c.id), label: c.name }))}
      onChange={onChange} placeholder={placeholder} disabled={disabled}
      onCreate={create} createLabel="Add client"
      createFields={[{ key: 'website', label: 'Website', required: true, placeholder: 'acme.ai',
        hint: 'The domain drives intelligence sweeps and the public advert, so it is captured now rather than later.' }]} />
  );
}
